import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSearch } from '../../context/SearchContext';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { FaUser, FaMusic, FaUsers, FaList, FaMicrophone } from 'react-icons/fa';
import { useMusic } from '../../context/MusicContext';
import { useProfile } from '../../context/ProfileContext';
import UserProfilePicture from '../profile/UserProfilePicture';

const iconMap = {
  user: <FaUsers className="mr-3 text-gray-400 dark:text-zinc-500" />,
  artist: <FaMicrophone className="mr-3 text-gray-400 dark:text-zinc-500" />,
  song: <FaMusic className="mr-3 text-gray-400 dark:text-zinc-500" />,
  playlist: <FaList className="mr-3 text-gray-400 dark:text-zinc-500" />,
};

const pathMap = {
  user: '/profile/',
  artist: '/profile/',
  song: '/song/', 
  playlist: '/playlist/',
};

const SearchResults = ({ results, isLoading }) => {
  const navigate = useNavigate();
  const { clearSearch } = useSearch(); 
  const { setQueueAndPlay } = useMusic(); 
  const { isDarkMode } = useProfile(); // 🔑 Extract dark mode state
  const [isHydrating, setIsHydrating] = useState(false);

  if (isLoading) {
    return (
      <p className={`text-center mt-4 transition-colors ${
        isDarkMode ? "text-zinc-500" : "text-gray-500"
      }`}>
        Searching...
      </p>
    );
  }

  if (results.length === 0) {
    return (
      <p className={`text-center mt-4 transition-colors ${
        isDarkMode ? "text-zinc-500" : "text-gray-500"
      }`}>
        No results found.
      </p>
    );
  }

  const handleResultClick = async (item) => {
    setIsHydrating(true);

    if (item.type === 'song') {
      try {
        const db = getFirestore();
        const songRef = doc(db, "songs", item.id);
        const docSnap = await getDoc(songRef);

        if (docSnap.exists()) {
          const fullSongData = { id: docSnap.id, ...docSnap.data() };
          setQueueAndPlay([fullSongData], 0, 'Search');
          clearSearch(); 
        } else {
          throw new Error("Song not found.");
        }
      } catch (error) {
        console.error("Failed to play song:", error);
        setIsHydrating(false); 
      }
    } else {
      const path = pathMap[item.type] + item.id;
      navigate(path);
      clearSearch();
    }
  };

  return (
    <div className="mt-4 space-y-2 z-50">
      {results.map((item) => (
        <div
          key={`${item.type}-${item.id}`}
          onClick={() => handleResultClick(item)}
          className={`flex items-center p-3 rounded-lg cursor-pointer transition-colors duration-250 ${
            // 🔑 Theme-swappable container backgrounds and subtle borders
            isDarkMode 
              ? "bg-zinc-800 border border-gray-700 hover:bg-zinc-850 text-white" 
              : "bg-gray-50 hover:bg-gray-100 text-gray-900"
          }`}
        >
          <div className="mr-4 flex-shrink-0">
            <UserProfilePicture 
              photoURL={item.imageUrl} 
              sizeClass="w-12 h-12"
              shape="square" // 👈 Matches the current modern square search aesthetics
              isPremium={item.type === 'user' && item.isPremium === true} // 🌟 Strict Type Protection Gate
            />
          </div>
          
          <div className="flex-grow min-w-0">
            {/* Main title text inherits wrapper color */}
            <p className="font-semibold truncate">{item.title}</p>
            
            {/* Subtitles swap elegantly from gray-500 to a warm muted zinc */}
            <p className={`text-sm truncate transition-colors ${
              isDarkMode ? "text-zinc-450" : "text-gray-500"
            }`}>
              {item.subtitle}
            </p>
          </div>
          {iconMap[item.type]}
        </div>
      ))}
    </div>
  );
};

export default SearchResults;